import React, { useState, useEffect, useRef } from "react";
import Burrs1 from "./Images/Burrs1.png";
import Ecmm1 from "./Images/Ecmm1.png";

// ---- LOCAL STORAGE PERSISTENCE HELPERS ----
// All dropdown Add/Remove lists are saved to localStorage under this prefix
// so that any additions/removals survive a browser refresh or reload.
const STORAGE_PREFIX = "cosmoAutomation_";

// Bump this whenever the hardcoded default lists below (Person, Projects,
// etc.) are changed in code. Every browser caches its own Add/Remove lists
// in localStorage, so without this, a code change (e.g. fixing the Project
// list or removing a person) would only show up for brand-new browsers -
// everyone else would keep seeing their old cached list forever. On version
// mismatch we wipe the stale cached lists so every user re-syncs to the
// current defaults below.
const DATA_VERSION = "3";

const loadListFromStorage = (key, fallback) => {
  try {
    const versionKey = STORAGE_PREFIX + "dataVersion";
    const storedVersion = window.localStorage.getItem(versionKey);

    if (storedVersion !== DATA_VERSION) {
      // Defaults changed since this browser last visited - clear every
      // previously cached Add/Remove list so this browser falls back to
      // the fresh defaults, then stamp the new version.
      Object.keys(window.localStorage)
        .filter((k) => k.startsWith(STORAGE_PREFIX) && k !== versionKey)
        .forEach((k) => window.localStorage.removeItem(k));

      window.localStorage.setItem(versionKey, DATA_VERSION);

      return fallback;
    }

    const saved = window.localStorage.getItem(STORAGE_PREFIX + key);

    if (saved) {
      const parsed = JSON.parse(saved);

      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch (err) {
    console.error(`Error loading "${key}" from localStorage:`, err);
  }

  return fallback;
};

const saveListToStorage = (key, list) => {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(list));
  } catch (err) {
    console.error(`Error saving "${key}" to localStorage:`, err);
  }
};

// ---- CROSS-DEVICE SYNC (pure frontend, via JSONBin.io) ----
// localStorage only lives on ONE browser/device, so an Add/Remove done on
// a laptop was never visible on a phone (and vice-versa). To fix that
// WITHOUT writing any custom backend route, every dropdown list (Person,
// Projects, Machines, Equipments, Area, ProjectWorks, Shifts, Consumed,
// WorkStatus, DesignActivities, WFHNames) is also stored in a single free
// JSONBin.io "bin" (a hosted JSON file reachable over plain fetch()).
// Every add/remove pushes the full updated set of lists to that bin, and
// the current lists are pulled from the bin whenever this page loads - so
// every device/browser that opens this page sees the same options.
// localStorage (above) is kept only as a same-device fallback/cache in
// case the network/JSONBin is briefly unavailable.
//
// SETUP (one-time, ~2 minutes):
//   1. Go to https://jsonbin.io and create a free account.
//   2. Dashboard -> "Create Bin" -> paste this as the starting content:
//        {"persons": [], "projects": [], "machines": [], "equipments": [],
//         "areas": [], "works": [], "shifts": [], "consumedItems": [],
//         "workStatuses": [], "designActivities": [], "wfhNames": []}
//      Save it, then copy the Bin ID shown in the bin's URL
//      (looks like 64f1a2b3c9e77b1a2c3d4e5f).
//   3. Dashboard -> "API Keys" -> copy your X-Master-Key.
//   4. Paste both values into JSONBIN_BIN_ID and JSONBIN_API_KEY below.
// That's it - no server code needed. Every user's browser now reads from
// and writes to the same shared bin directly.
const JSONBIN_BIN_ID = "6a916555f5f4af5e294d64c5";
const JSONBIN_API_KEY = "$2a$10$q5yFzvEqzU8NYKWFheK9S.lfRU2FwruXXyEdcgjHZZhU.S2mMvjQK";
const JSONBIN_BASE_URL = `https://api.jsonbin.io/v3/b/${JSONBIN_BIN_ID}`;

// Fetches the full current bin content. Returns {} if the request fails
// (offline, JSONBin down, placeholder credentials not filled in yet, etc.)
// so callers can fall back gracefully to localStorage/defaults.
const fetchFullBin = async () => {
  try {
    const response = await fetch(`${JSONBIN_BASE_URL}/latest`, {
      method: "GET",
      headers: {
        "X-Master-Key": JSONBIN_API_KEY,
        "X-Bin-Meta": "false",
      },
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) return {};
    // With X-Bin-Meta: false, JSONBin returns the record itself directly.
    return result || {};
  } catch (e) {
    console.error("Failed to fetch shared dropdown lists from JSONBin:", e);
    return {};
  }
};

// Pushes a full bin object back to JSONBin (a straight replace - JSONBin
// has no "update one field" endpoint).
const pushFullBin = async (fullBinContent) => {
  try {
    const response = await fetch(JSONBIN_BASE_URL, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "X-Master-Key": JSONBIN_API_KEY,
      },
      body: JSON.stringify(fullBinContent),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(result.message || "JSONBin rejected the change");
    }
    return true;
  } catch (e) {
    // Saved locally on this device (via saveListToStorage), but the shared
    // bin couldn't be reached so other devices won't see it yet. Logged
    // only - the Add/Remove itself still completes normally for this user.
    console.error("Failed to sync dropdown list change to JSONBin:", e);
    return false;
  }
};

// Given one list's field name (e.g. "persons") and its brand-new value,
// fetches the latest full bin first (so we don't clobber every OTHER
// list's data with a stale copy), merges this one field in, and pushes
// the whole object back.
const syncListToServer = async (fieldName, updatedList) => {
  const latestBin = await fetchFullBin();
  const mergedBin = {
    ...latestBin,
    [fieldName]: updatedList,
  };
  await pushFullBin(mergedBin);
};

// ---- SEARCHABLE / FILTERABLE DROPDOWN ----
// Drop-in replacement for a native <select>. Behaves like a combobox:
// clicking it opens a filterable list, typing letters filters the options,
// arrow keys move the highlight, Enter picks the highlighted option, and
// Escape / clicking outside closes it without changing the value.
const dropdownListStyle = {
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  marginTop: "6px",
  maxHeight: "280px",
  overflowY: "auto",
  background: "rgba(20,8,45,0.98)",
  border: "1px solid rgba(255,234,0,0.45)",
  borderRadius: "16px",
  zIndex: 5000,
  boxShadow: "0 14px 34px rgba(0,0,0,0.55)",
  backdropFilter: "blur(12px)",
};

const dropdownItemStyle = {
  padding: "14px 20px",
  fontSize: "18px",
  color: "#ffffff",
  cursor: "pointer",
  borderBottom: "1px solid rgba(255,255,255,0.08)",
};

const SearchableSelect = ({ name, value, onChange, options, placeholder, style }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = options.filter((opt) =>
    opt.toLowerCase().includes(search.toLowerCase())
  );

  const selectValue = (val) => {
    onChange({ target: { name, value: val } });
    setIsOpen(false);
    setSearch("");
  };

  const openDropdown = () => {
    setIsOpen(true);
    setSearch("");
    setHighlightedIndex(0);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        openDropdown();
        e.preventDefault();
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        filteredOptions.length ? (prev + 1) % filteredOptions.length : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        filteredOptions.length
          ? (prev - 1 + filteredOptions.length) % filteredOptions.length
          : 0
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filteredOptions[highlightedIndex] !== undefined) {
        selectValue(filteredOptions[highlightedIndex]);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsOpen(false);
      setSearch("");
    }
  };

  return (
    <div ref={wrapperRef} style={{ position: "relative", width: "100%" }}>
      <input
        type="text"
        autoComplete="off"
        value={isOpen ? search : value || ""}
        placeholder={placeholder}
        onFocus={openDropdown}
        onClick={openDropdown}
        onChange={(e) => {
          setSearch(e.target.value);
          setHighlightedIndex(0);
          if (!isOpen) setIsOpen(true);
        }}
        onKeyDown={handleKeyDown}
        style={style}
      />

      {isOpen && (
        <div style={dropdownListStyle}>
          <div
            className="searchable-option"
            onMouseDown={() => selectValue("")}
            style={{ ...dropdownItemStyle, opacity: 0.75, fontStyle: "italic" }}
          >
            {placeholder}
          </div>

          {filteredOptions.length === 0 && (
            <div style={{ ...dropdownItemStyle, opacity: 0.6, cursor: "default" }}>
              No matches found
            </div>
          )}

          {filteredOptions.map((opt, idx) => (
            <div
              key={opt}
              className="searchable-option"
              onMouseDown={() => selectValue(opt)}
              onMouseEnter={() => setHighlightedIndex(idx)}
              style={{
                ...dropdownItemStyle,
                background:
                  idx === highlightedIndex ? "rgba(255,234,0,0.18)" : "transparent",
              }}
            >
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CosmoAutomation = () => {
  const data = {
    Person: ["Anandhu", "Uday", "Dinesh", "Mamtha", "Hari", "Shambhu"],

    Projects: [
      "RR precision",
      "MT Auto",
      "Tafe Bhopal",
    
    ],

    ProjectWorks: [
      "ECM trial",
      "Machine Making",
      "Machine Maintenance",
      "Fixture maintenance",
      "Fixture making",
      "Electrolyte making",
      "ECM Machine Testing",
      "Electrical Wiring",
      "PCB Programming",
      "Routing",
      "Drilling",
      "Tapping",
      "Electrode Machining",
      "Machine Buffing",
      "Machine Cleaning",
      "Machine Welding",
      "Laser Cutting",
      "Machine Sheet Metal Bending",
      "Machine Sheet Metal Cutting",
      "Documentation",
    ],

    Machines: [
      "Routing",
      "Grinding",
      "Buffing",
      "Drilling",
      "Milling",
      "ECM 300",
      "Burrgon",
      "Hand Drilling",
      "Hand Cutting",
      "Laser Welding",
      "Arc Welding",
      "Hydraulic Press",
      "Lathe",
      "Laser Marking",
      "Packing",
      "Laser Wielding Machine",
      "Router",
      "Hand Tap",
      "Milling Machine",
      "Angle Grinder",
      "Table Drilling Machine",
      "Pillar Drilling Machine",
      "Packing Machine",
      "ECM 300",
      "Burrgon",
      "Laser Marking Machine",
      "Circular saw",
      "CNC Turning -GEDEE WEILER",
      "CNC Turning -JYOTI",
      "CNC Turning -PAVAN",
      "Soldering Iron",
      "Hot Air Gun",
      "Hand Power Drill",
      "Arc Welding Machine",
      "Oscilloscope",
      "Chemical filtration equipment",
      "Rolling Machine",
      "ScrewDriver",
      "Allen Key",
    ],

    Equipments: [
      "Diode laser",
      "Ball mill",
      "Vacuum chamber",
      "Conductivity measurement instrument",
      "Foil cutting saw",
      "Roll calendering machine ",
      "Rolling machine",
      "Painting booth",
      "Hydraulic press",
      "Vacuum filter",
      "Magnetic stirrer",
    ],

    Shifts: ["1st", "2nd", "General"],

    Area: [
      "Cosmos 1",
      "Cosmos 2",
      "Cosmos 3",
      "Cosmos 4",
      "Cosmos 5",
    ],

    Consumed: [
      "Wire",
      "SS Nuts",
      "SS Bolts",
      "Washers",
      "Glue",
      "Insulation Tape",
      "Connecting Pins",
      "Sleeves",
      "PVC Sheet",
      "PP Sheet",
      "Polycarbonate sheet",
      "SS Rod",
      "SS Sheet",
      "Brass Rod",
      "Allen Bolt",
      "SS Screw",
    ],

    WorkStatus: [
      "Started",
      "Take over",
      "Work In Progress",
      "Hand over",
      "Finished",
    ],

    WFHNames: ["Mamtha", "Jatin Sharma", "Harshit Karmakar", "Hajara", "Manisha","Tejaswini"],

    DesignActivities: [
      "Machine and Fixture Design",
      "Purchase Activities",
      "ERP",
      "Installation",
      "Packing",
    ],
  };

  // MACHINE ICONS
  const machineImages = [Burrs1, Ecmm1];

  // MACHINE PICTURES
  const machinePics = [Burrs1, Ecmm1];

  const [form, setForm] = useState({
    person: "",
    project: "",
    work: "",
    machine: "",
    shift_name: "",
    area: "",
    consumed: "",
    materialConsumed: "",
    status: "",
    workDescription: "",
    startTime: "",
    endTime: "",
    quantity: "",
    rejection: "",
    equipment: "",
    wfhName: "",
    designActivity: "",
    remarks: "",
  });

  const [botMessage, setBotMessage] = useState(
    "Welcome to Cosmos Automation"
  );

  const [pointer, setPointer] = useState({
    x: 0,
    y: 0,
  });

  useEffect(() => {
    const defaultMessages = [
      "Welcome to Cosmos Automation",
      "Select Person and Project",
      "Track Production Activities",
      "Touch and Move for Smart Feel",
      "Submit Manufacturing Details",
    ];

    let i = 0;

    const interval = setInterval(() => {
      if (form.person) {
        const personMessages = [
          `Hi ${form.person}, Welcome to Cosmos Automation`,
          `${form.person}, Track Production Activities`,
          `${form.person}, Submit Manufacturing Details`,
          `${form.person}, Have a Productive Shift`,
        ];

        i = (i + 1) % personMessages.length;

        setBotMessage(personMessages[i]);
      } else {
        i = (i + 1) % defaultMessages.length;

        setBotMessage(defaultMessages[i]);
      }
    }, 3500);

    return () => clearInterval(interval);
  }, [form.person]);

  const handleMove = (e) => {
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;

    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    setPointer({
      x: clientX,
      y: clientY,
    });
  };

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async () => {
    try {
      const res = await fetch("https://www.d-rosa.ai/DBWRRTAPI", {
        method: "POST",

        headers: {
          "Content-Type": "application/json",
        },

        body: JSON.stringify(form),
      });

      const result = await res.json();

      if (res.ok) {
        alert("Data Stored Successfully");

        setForm({
          person: "",
          project: "",
          work: "",
          machine: "",
          shift_name: "",
          area: "",
          consumed: "",
          materialConsumed: "",
          status: "",
          workDescription: "",
          startTime: "",
          endTime: "",
          quantity: "",
          rejection: "",
          equipment: "",
          wfhName: "",
          designActivity: "",
          remarks: "",
        });
      } else {
        alert(result.error);
      }
    } catch (err) {
      console.error(err);

      alert("Server Error");
    }
  };


// ---- DATE FILTER HELPER ----
// Normalizes any date-like value (ISO string, timestamp, Date object) down
// to a "date only" JS Date (time set to 00:00:00) so that comparisons are
// not affected by the time-of-day portion of "created_at".
const toDateOnly = (value) => {
  if (!value) return null;

  const d = new Date(value);

  if (isNaN(d.getTime())) return null;

  d.setHours(0, 0, 0, 0);
  return d;
};

// ---- CSV DATE FORMAT HELPER ----
// Reformats a created_at value for the CSV export only, as "DD-MM-YYYY"
// (date, month, year) - e.g. "2026-08-13 11:54:00" -> "13-08-2026 11:54:00".
// Parses the raw string directly (rather than going through `new Date()`,
// which can shift the date by a day depending on the browser's timezone)
// so the day/month/year printed in the CSV always matches what was stored.
// Only used when building the CSV row values below - the date filtering
// above (toDateOnly) is untouched and keeps comparing real Date objects.
const formatDateForCSV = (value) => {
  if (!value) return "";
  const str = String(value).trim();

  // "YYYY-MM-DD..." (MySQL DATETIME / ISO) - year comes first.
  let m = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) {
    const [, y, mo, d] = m;
    return reorderToDMY(str, y, mo, d);
  }

  // "DD-MM-YYYY..." - already day/month first, just zero-pad.
  m = str.match(/^(\d{1,2})-(\d{1,2})-(\d{4})/);
  if (m) {
    const [, d, mo, y] = m;
    return reorderToDMY(str, y, mo, d);
  }

  // "DD/MM/YYYY..." - same as above but slash-separated.
  m = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (m) {
    const [, d, mo, y] = m;
    return reorderToDMY(str, y, mo, d);
  }

  // Fallback: let the JS Date parser take a shot at anything else.
  const parsed = new Date(str);
  if (!isNaN(parsed.getTime())) {
    const y = parsed.getFullYear();
    const mo = String(parsed.getMonth() + 1).padStart(2, "0");
    const d = String(parsed.getDate()).padStart(2, "0");
    return reorderToDMY(str, y, mo, d);
  }

  // Couldn't parse it at all - leave the original value untouched rather
  // than dropping it from the export.
  return str;
};

// Builds the final "DD-MM-YYYY" (optionally " HH:mm[:ss]") string, keeping
// any time-of-day portion found in the original raw value.
const reorderToDMY = (originalStr, y, mo, d) => {
  const formattedDate = `${String(d).padStart(2, "0")}-${String(mo).padStart(
    2,
    "0"
  )}-${y}`;
  const timeMatch = originalStr.match(/(\d{1,2}:\d{2}(:\d{2})?)/);
  return timeMatch ? `${formattedDate} ${timeMatch[1]}` : formattedDate;
};

// filterOption: "all" | "single" | "range"
// singleDate: "YYYY-MM-DD" (used when filterOption === "single")
// rangeStart / rangeEnd: "YYYY-MM-DD" (used when filterOption === "range")
const downloadCSV = async (
  filterOption = "all",
  singleDate = null,
  rangeStart = null,
  rangeEnd = null
) => {
  try {
    const response = await fetch("https://www.d-rosa.ai/DBGETALLCOSMOAPI", {
      method: "GET",
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      alert(result.error || "Failed to fetch data from server.");
      return;
    }

    let allData = result.data || [];

    if (allData.length === 0) {
      alert("No data recorded yet in the database.");
      return;
    }

    // ---- CONDITION 1: single date filter (e.g. only August 11th) ----
    if (filterOption === "single" && singleDate) {
      const target = toDateOnly(singleDate);

      allData = allData.filter((row) => {
        const rowDate = toDateOnly(row.created_at);
        return rowDate && target && rowDate.getTime() === target.getTime();
      });
    }

    // ---- CONDITION 2: date range filter (e.g. August 10th to August 12th) ----
    else if (filterOption === "range" && rangeStart && rangeEnd) {
      const start = toDateOnly(rangeStart);
      const end = toDateOnly(rangeEnd);

      allData = allData.filter((row) => {
        const rowDate = toDateOnly(row.created_at);
        return (
          rowDate &&
          start &&
          end &&
          rowDate.getTime() >= start.getTime() &&
          rowDate.getTime() <= end.getTime()
        );
      });
    }

    if (allData.length === 0) {
      alert("No records found for the selected date filter.");
      return;
    }

    // Fixed column order (spreadsheet letters F,K,G,A,E,P,L,D,N,H,M,I,J):
    // materialConsumed, remarks, newPerson, area, machine, workDescription,
    // shift_name, endTime, status, project, startTime, quantity, rejection
    const headers = [
      "newPerson",
      "shift_name",
      "project",
      "area",
      "machine",
      "equipment",
      "workDescription",
      "startTime",
      "endTime",
      "materialConsumed",
      "quantity",
      "status",
      "rejection",
      "remarks",
      "created_at",
    ];

    const csvRows = [];
    csvRows.push(headers.join(","));

    allData.forEach((row) => {
      const values = headers.map((header) => {
        // Store the created_at date as date,month,year (DD-MM-YYYY) in the
        // CSV, regardless of whatever order/format the backend sent it in.
        const value =
          header === "created_at"
            ? formatDateForCSV(row[header])
            : row[header];
        return `"${value !== undefined && value !== null ? value : ""}"`;
      });
      csvRows.push(values.join(","));
    });

    const csvContent = csvRows.join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);

    // Build a descriptive filename based on the filter that was applied
    let fileName = "CosmosAutomation_AllEmployees.csv";

    if (filterOption === "single" && singleDate) {
      fileName = `CosmosAutomation_${singleDate}.csv`;
    } else if (filterOption === "range" && rangeStart && rangeEnd) {
      fileName = `CosmosAutomation_${rangeStart}_to_${rangeEnd}.csv`;
    }

    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error(error);
    alert("Server Error while fetching data for download.");
  }
};

// --- Access control: Download CSV is restricted to Team Leader / HR ---
const TEAM_LEADER_HR_PIN = "2580";

// --- Access control: Add/Remove on any dropdown list is restricted to
// Team Leader / HR. Every Add/Remove button calls this first; if the PIN
// is missing or wrong, the action is blocked and the list is left untouched.
const verifyHRAccess = () => {
  const enteredPin = window.prompt(
    "Adding or removing dropdown options is restricted to Team Leader / HR.\nEnter the access PIN to continue:"
  );

  if (enteredPin === null) return false;

  if (enteredPin.trim() !== TEAM_LEADER_HR_PIN) {
    alert("Access Denied: Only Team Leader / HR can add or remove dropdown options.");
    return false;
  }

  return true;
};

const handleDownloadClick = () => {
  const enteredPin = window.prompt(
    "This report is restricted to Team Leader / HR.\nEnter the access PIN to continue:"
  );

  if (enteredPin === null) return;

  if (enteredPin.trim() !== TEAM_LEADER_HR_PIN) {
    alert("Access Denied: Only Team Leader / HR can download this report.");
    return;
  }

  // ---- Ask which date filter to apply before downloading ----
  const filterChoice = window.prompt(
    "Choose CSV filter:\n" +
      "1 = All records\n" +
      "2 = Single date (e.g. 2026-08-11)\n" +
      "3 = Date range (e.g. 2026-08-10 to 2026-08-12)\n\n" +
      "Enter 1, 2, or 3:"
  );

  if (filterChoice === null) return;

  const choice = filterChoice.trim();

  if (choice === "2") {
    // CONDITION 1: single date, e.g. only August 11th
    const singleDate = window.prompt(
      "Enter the date to filter (YYYY-MM-DD), e.g. 2026-08-11:"
    );

    if (!singleDate) return;

    downloadCSV("single", singleDate.trim());
  } else if (choice === "3") {
    // CONDITION 2: date range, e.g. August 10th to August 12th
    const rangeStart = window.prompt(
      "Enter start date (YYYY-MM-DD), e.g. 2026-08-10:"
    );

    if (!rangeStart) return;

    const rangeEnd = window.prompt(
      "Enter end date (YYYY-MM-DD), e.g. 2026-08-12:"
    );

    if (!rangeEnd) return;

    downloadCSV("range", null, rangeStart.trim(), rangeEnd.trim());
  } else {
    downloadCSV("all");
  }
};

  const [persons, setPersons] = useState(() =>
    loadListFromStorage("persons", data.Person)
  );
  const [selectedPerson, setSelectedPerson] = useState("");
  const [newPerson, setNewPerson] = useState("");

  useEffect(() => {
    saveListToStorage("persons", persons);
  }, [persons]);

  // Add Person
  const handleAddPerson = async () => {
  if (!verifyHRAccess()) return;

  const name = newPerson.trim();

  if (!name) {
    alert("Enter a person name");
    return;
  }

  if (persons.includes(name)) {
    alert("Person already exists");
    return;
  }

  const updatedPersons = [...persons, name];

  setPersons(updatedPersons);

  setForm((prev) => ({
    ...prev,
    person: name,
  }));

  setSelectedPerson(name);
  setNewPerson("");

  await syncListToServer("persons", updatedPersons);
};

  // Delete Selected Person
  const handleDeletePerson = async () => {
  if (!verifyHRAccess()) return;

  if (!selectedPerson) {
    alert("Select a person to delete");
    return;
  }

  const updatedPersons = persons.filter(
    (person) => person !== selectedPerson
  );

  setPersons(updatedPersons);

  if (form.person === selectedPerson) {
    setForm((prev) => ({
      ...prev,
      person: "",
    }));
  }

  setSelectedPerson("");

  await syncListToServer("persons", updatedPersons);
};



 const [projects, setProjects] = useState(() =>
    loadListFromStorage("projects", data.Projects)
  );
  const [newProject, setNewProject] = useState("");

  useEffect(() => {
    saveListToStorage("projects", projects);
  }, [projects]);

  

  // ADD project
  const addProject = async () => {
    if (!verifyHRAccess()) return;

    const value = newProject.trim();

    if (value === "") {
      alert("Enter project name");
      return;
    }

    if (projects.includes(value)) {
      alert("Project already exists");
      return;
    }

    const updatedProjects = [...projects, value];
    setProjects(updatedProjects);
    setNewProject("");

    await syncListToServer("projects", updatedProjects);
  };

  // DELETE selected project
  const deleteProject = async () => {
    if (!verifyHRAccess()) return;

    if (!form.project) {
      alert("Select a project to delete");
      return;
    }

    const updatedList = projects.filter(
      (item) => item !== form.project
    );

    setProjects(updatedList);

    setForm((prev) => ({ ...prev, project: "" }));

    await syncListToServer("projects", updatedList);
  };


const [machines, setMachines] = useState(() =>
  loadListFromStorage("machines", data.Machines)
);
const [newMachine, setNewMachine] = useState("");

useEffect(() => {
  saveListToStorage("machines", machines);
}, [machines]);

const addMachine = async () => {
  if (!verifyHRAccess()) return;

  const value = newMachine.trim();

  if (value === "") {
    alert("Enter machine name");
    return;
  }

  if (machines.includes(value)) {
    alert("Machine already exists");
    return;
  }

  const updatedMachines = [...machines, value];
  setMachines(updatedMachines);
  setNewMachine("");

  await syncListToServer("machines", updatedMachines);
};

const deleteMachine = async () => {
  if (!verifyHRAccess()) return;

  if (!form.machine) {
    alert("Select a machine to remove");
    return;
  }

  const updatedMachines = machines.filter((m) => m !== form.machine);
  setMachines(updatedMachines);

  setForm({
    ...form,
    machine: "",
  });

  await syncListToServer("machines", updatedMachines);
};



const [equipments, setEquipments] = useState(() =>
  loadListFromStorage("equipments", data.Equipments)
);
const [newEquipment, setNewEquipment] = useState("");

useEffect(() => {
  saveListToStorage("equipments", equipments);
}, [equipments]);

const addEquipment = async () => {
  if (!verifyHRAccess()) return;

  const value = newEquipment.trim();

  if (value === "") {
    alert("Enter equipment name");
    return;
  }

  if (equipments.includes(value)) {
    alert("Equipment already exists");
    return;
  }

  const updatedEquipments = [...equipments, value];
  setEquipments(updatedEquipments);
  setNewEquipment("");

  await syncListToServer("equipments", updatedEquipments);
};

const deleteEquipment = async () => {
  if (!verifyHRAccess()) return;

  if (!form.equipment) {
    alert("Select an equipment to remove");
    return;
  }

  const updatedEquipments = equipments.filter((eq) => eq !== form.equipment);
  setEquipments(updatedEquipments);

  setForm({
    ...form,
    equipment: "",
  });

  await syncListToServer("equipments", updatedEquipments);
};



const [areas, setAreas] = useState(() =>
  loadListFromStorage("areas", data.Area)
);
const [newArea, setNewArea] = useState("");
const [selectedArea, setSelectedArea] = useState("");

useEffect(() => {
  saveListToStorage("areas", areas);
}, [areas]);

const addArea = async () => {
  if (!verifyHRAccess()) return;

  const value = newArea.trim();

  if (!value) {
    alert("Enter an Area");
    return;
  }

  if (areas.includes(value)) {
    alert("Area already exists");
    return;
  }

  const updatedAreas = [...areas, value];
  setAreas(updatedAreas);
  setNewArea("");

  await syncListToServer("areas", updatedAreas);
};


const removeArea = async () => {
  if (!verifyHRAccess()) return;

  if (!selectedArea) {
    alert("Select an Area to remove");
    return;
  }

  const updatedAreas = areas.filter((a) => a !== selectedArea);
  setAreas(updatedAreas);

  if (form.area === selectedArea) {
    setForm({
      ...form,
      area: "",
    });
  }

  setSelectedArea("");

  await syncListToServer("areas", updatedAreas);
};




// SELECT WORK
const [works, setWorks] = useState(() =>
  loadListFromStorage("works", data.ProjectWorks)
);
const [newWork, setNewWork] = useState("");

useEffect(() => {
  saveListToStorage("works", works);
}, [works]);

const addWork = async () => {
  if (!verifyHRAccess()) return;

  const value = newWork.trim();

  if (!value) {
    alert("Enter work");
    return;
  }

  if (works.includes(value)) {
    alert("Work already exists");
    return;
  }

  const updatedWorks = [...works, value];
  setWorks(updatedWorks);
  setNewWork("");

  await syncListToServer("works", updatedWorks);
};

const removeWork = async () => {
  if (!verifyHRAccess()) return;

  if (!form.work) {
    alert("Select a work to remove");
    return;
  }

  const updatedWorks = works.filter((w) => w !== form.work);
  setWorks(updatedWorks);

  setForm((prev) => ({
    ...prev,
    work: "",
  }));

  await syncListToServer("works", updatedWorks);
};



// SELECT SHIFT
const [shifts, setShifts] = useState(() =>
  loadListFromStorage("shifts", data.Shifts)
);
const [newShift, setNewShift] = useState("");

useEffect(() => {
  saveListToStorage("shifts", shifts);
}, [shifts]);

const addShift = async () => {
  if (!verifyHRAccess()) return;

  const value = newShift.trim();

  if (!value) {
    alert("Enter shift");
    return;
  }

  if (shifts.includes(value)) {
    alert("Shift already exists");
    return;
  }

  const updatedShifts = [...shifts, value];
  setShifts(updatedShifts);
  setNewShift("");

  await syncListToServer("shifts", updatedShifts);
};

const removeShift = async () => {
  if (!verifyHRAccess()) return;

  if (!form.shift_name) {
    alert("Select a shift to remove");
    return;
  }

  const updatedShifts = shifts.filter((s) => s !== form.shift_name);
  setShifts(updatedShifts);

  setForm((prev) => ({
    ...prev,
    shift_name: "",
  }));

  await syncListToServer("shifts", updatedShifts);
};



// CONSUMED ITEM
const [consumedItems, setConsumedItems] = useState(() =>
  loadListFromStorage("consumedItems", data.Consumed)
);
const [newConsumedItem, setNewConsumedItem] = useState("");

useEffect(() => {
  saveListToStorage("consumedItems", consumedItems);
}, [consumedItems]);

const addConsumedItem = async () => {
  if (!verifyHRAccess()) return;

  const value = newConsumedItem.trim();

  if (!value) {
    alert("Enter consumed item");
    return;
  }

  if (consumedItems.includes(value)) {
    alert("Consumed item already exists");
    return;
  }

  const updatedConsumedItems = [...consumedItems, value];
  setConsumedItems(updatedConsumedItems);
  setNewConsumedItem("");

  await syncListToServer("consumedItems", updatedConsumedItems);
};

const removeConsumedItem = async () => {
  if (!verifyHRAccess()) return;

  if (!form.consumed) {
    alert("Select a consumed item to remove");
    return;
  }

  const updatedConsumedItems = consumedItems.filter((c) => c !== form.consumed);
  setConsumedItems(updatedConsumedItems);

  setForm((prev) => ({
    ...prev,
    consumed: "",
  }));

  await syncListToServer("consumedItems", updatedConsumedItems);
};



// WORK STATUS
const [workStatuses, setWorkStatuses] = useState(() =>
  loadListFromStorage("workStatuses", data.WorkStatus)
);
const [newWorkStatus, setNewWorkStatus] = useState("");

useEffect(() => {
  saveListToStorage("workStatuses", workStatuses);
}, [workStatuses]);

const addWorkStatus = async () => {
  if (!verifyHRAccess()) return;

  const value = newWorkStatus.trim();

  if (!value) {
    alert("Enter work status");
    return;
  }

  if (workStatuses.includes(value)) {
    alert("Work status already exists");
    return;
  }

  const updatedWorkStatuses = [...workStatuses, value];
  setWorkStatuses(updatedWorkStatuses);
  setNewWorkStatus("");

  await syncListToServer("workStatuses", updatedWorkStatuses);
};

const removeWorkStatus = async () => {
  if (!verifyHRAccess()) return;

  if (!form.status) {
    alert("Select a work status to remove");
    return;
  }

  const updatedWorkStatuses = workStatuses.filter((s) => s !== form.status);
  setWorkStatuses(updatedWorkStatuses);

  setForm((prev) => ({
    ...prev,
    status: "",
  }));

  await syncListToServer("workStatuses", updatedWorkStatuses);
};



// DESIGN / OTHER ACTIVITIES
const [designActivities, setDesignActivities] = useState(() =>
  loadListFromStorage("designActivities", data.DesignActivities)
);
const [newDesignActivity, setNewDesignActivity] = useState("");

useEffect(() => {
  saveListToStorage("designActivities", designActivities);
}, [designActivities]);

const addDesignActivity = async () => {
  if (!verifyHRAccess()) return;

  const value = newDesignActivity.trim();

  if (!value) {
    alert("Enter activity");
    return;
  }

  if (designActivities.includes(value)) {
    alert("Activity already exists");
    return;
  }

  const updatedDesignActivities = [...designActivities, value];
  setDesignActivities(updatedDesignActivities);
  setNewDesignActivity("");

  await syncListToServer("designActivities", updatedDesignActivities);
};

const removeDesignActivity = async () => {
  if (!verifyHRAccess()) return;

  if (!form.designActivity) {
    alert("Select an activity to remove");
    return;
  }

  const updatedDesignActivities = designActivities.filter(
    (d) => d !== form.designActivity
  );
  setDesignActivities(updatedDesignActivities);

  setForm((prev) => ({
    ...prev,
    designActivity: "",
  }));

  await syncListToServer("designActivities", updatedDesignActivities);
};



// WFH NAMES
const [wfhNames, setWfhNames] = useState(() =>
  loadListFromStorage("wfhNames", data.WFHNames)
);
const [newWfhName, setNewWfhName] = useState("");

useEffect(() => {
  saveListToStorage("wfhNames", wfhNames);
}, [wfhNames]);

const addWfhName = async () => {
  if (!verifyHRAccess()) return;

  const value = newWfhName.trim();

  if (!value) {
    alert("Enter WFH name");
    return;
  }

  if (wfhNames.includes(value)) {
    alert("WFH name already exists");
    return;
  }

  const updatedWfhNames = [...wfhNames, value];
  setWfhNames(updatedWfhNames);
  setNewWfhName("");

  await syncListToServer("wfhNames", updatedWfhNames);
};

const removeWfhName = async () => {
  if (!verifyHRAccess()) return;

  if (!form.wfhName) {
    alert("Select a WFH name to remove");
    return;
  }

  const updatedWfhNames = wfhNames.filter((w) => w !== form.wfhName);
  setWfhNames(updatedWfhNames);

  setForm((prev) => ({
    ...prev,
    wfhName: "",
  }));

  await syncListToServer("wfhNames", updatedWfhNames);
};



// ---- PULL SHARED LISTS FROM JSONBIN ON MOUNT ----
// On first load, fetch the current Person/Projects/Machines/Equipments/
// Area/ProjectWorks/Shifts/Consumed/WorkStatus/DesignActivities/WFHNames
// lists from the shared bin so an Add or Remove done on ANY device shows
// up here too - not just the device that made the change. If the request
// fails (offline, JSONBin down, placeholder credentials not filled in
// yet), we simply keep whatever loadListFromStorage already put in state
// (localStorage cache or the hardcoded defaults), so the page still works.
useEffect(() => {
  let cancelled = false;

  const applyIfPresent = (fieldName, setList, storageKey) => {
    return (bin) => {
      const list = bin[fieldName];
      if (Array.isArray(list) && list.length > 0) {
        setList(list);
        saveListToStorage(storageKey, list);
      }
    };
  };

  const fetchServerLists = async () => {
    const latestBin = await fetchFullBin();
    if (cancelled || !latestBin) return;

    applyIfPresent("persons", setPersons, "persons")(latestBin);
    applyIfPresent("projects", setProjects, "projects")(latestBin);
    applyIfPresent("machines", setMachines, "machines")(latestBin);
    applyIfPresent("equipments", setEquipments, "equipments")(latestBin);
    applyIfPresent("areas", setAreas, "areas")(latestBin);
    applyIfPresent("works", setWorks, "works")(latestBin);
    applyIfPresent("shifts", setShifts, "shifts")(latestBin);
    applyIfPresent("consumedItems", setConsumedItems, "consumedItems")(latestBin);
    applyIfPresent("workStatuses", setWorkStatuses, "workStatuses")(latestBin);
    applyIfPresent("designActivities", setDesignActivities, "designActivities")(latestBin);
    applyIfPresent("wfhNames", setWfhNames, "wfhNames")(latestBin);
  };

  fetchServerLists();
  return () => {
    cancelled = true;
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);






  // BIGGER INPUTS
  const inputStyle = {
    padding: "24px",
    borderRadius: "20px",
    border: "1px solid rgba(255,255,255,0.18)",
    width: "100%",
    outline: "none",
    fontSize: "24px",
    background: "rgba(255,255,255,0.12)",
    boxSizing: "border-box",
    minHeight: "88px",
    color: "#ffffff",
    fontWeight: "700",
    transition: "0.3s",
    backdropFilter: "blur(12px)",
    boxShadow: "0 8px 20px rgba(0,0,0,0.25)",
  };

  const labelStyle = {
    marginBottom: "12px",
    fontWeight: "800",
    color: "#ffffff",
    fontSize: "24px",
  };

  return (
    <div
      onMouseMove={handleMove}
      onTouchMove={handleMove}
      style={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "20px",
        background:
          "linear-gradient(135deg,#03045e,#240046,#5a189a,#7209b7)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* CURSOR GLOW */}
      <div
        style={{
          position: "fixed",
          left: pointer.x - 100,
          top: pointer.y - 100,
          width: "200px",
          height: "200px",
          borderRadius: "50%",
          background: "rgba(255,255,255,0.12)",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 1,
        }}
      />

      {/* CURSOR POINTER */}
      <div
        style={{
          position: "fixed",
          top: pointer.y - 10,
          left: pointer.x - 10,
          width: "20px",
          height: "20px",
          borderRadius: "50%",
          background: "#ffea00",
          boxShadow: "0 0 25px #ffea00",
          pointerEvents: "none",
          zIndex: 99999,
        }}
      />

      {/* FLOATING MACHINE ICONS */}
      {machineImages.map((img, index) => (
        <img
          key={index}
          src={img}
          alt=""
          style={{
            position: "absolute",
            width: index % 2 === 0 ? "260px" : "220px",
            opacity: "0.12",
            animation: `float ${8 + index}s infinite`,
            filter: "drop-shadow(0 0 20px rgba(255,234,0,0.5))",

            top: index === 0 ? "5%" : "75%",
            left: index === 0 ? "5%" : "7%",
          }}
        />
      ))}

      {/* FACTORY BACKGROUND */}
      <img
        src={machinePics[1]}
        alt=""
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          objectFit: "cover",
          opacity: "0.10",
        }}
      />

      {/* AI ASSISTANT */}
      <div
        className="assistant-box"
        style={{
          position: "fixed",
          right: "15px",
          bottom: "15px",
          zIndex: 999,
          display: "flex",
          alignItems: "center",
          gap: "14px",
          background: "rgba(255,255,255,0.15)",
          padding: "15px 18px",
          borderRadius: "22px",
          backdropFilter: "blur(14px)",
          boxShadow: "0 10px 35px rgba(255,234,0,0.35)",
          border: "1px solid rgba(255,234,0,0.25)",
          animation: "floatBot 4s infinite",
          maxWidth: "350px",
        }}
      >
        <img
          src="https://cdn-icons-png.flaticon.com/512/4712/4712035.png"
          alt=""
          style={{
            width: "65px",
            height: "65px",
          }}
        />

        <div>
          <div
            style={{
              color: "#ffffff",
              fontWeight: "900",
              fontSize: "18px",
            }}
          >
            COSMOS AI ASSISTANT
          </div>

          <div
            style={{
              color: "#f1f1f1",
              marginTop: "6px",
              fontSize: "15px",
              lineHeight: "22px",
              fontWeight: "600",
            }}
          >
            {botMessage}
          </div>
        </div>
      </div>

      {/* MAIN CARD */}
      <div
        className="main-card"
        style={{
          width: "100%",
          maxWidth: "1500px",
          padding: "35px",
          borderRadius: "30px",
          background: "rgba(255,255,255,0.10)",
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(255,255,255,0.15)",
          boxShadow: "0 20px 50px rgba(0,0,0,0.4)",
          position: "relative",
          zIndex: 2,
          boxSizing: "border-box",
        }}
      >
        {/* TOP BAR */}
        <div
          style={{
            width: "100%",
            padding: "18px 24px",
            marginBottom: "20px",
            borderRadius: "18px",
            background: "rgba(255,255,255,0.10)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            boxSizing: "border-box",
            border: "1px solid rgba(255,255,255,0.12)",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              color: "#ffffff",
              fontWeight: "700",
              fontSize: "22px",
            }}
          >
            <div
              style={{
                width: "12px",
                height: "12px",
                borderRadius: "50%",
                background: "#ffea00",
                boxShadow: "0 0 15px #ffea00",
              }}
            />

            Production System Active
          </div>

          <div
            style={{
              color: "#ffffff",
              fontWeight: "800",
              letterSpacing: "1px",
              fontSize: "28px",
            }}
          >
            Work Entry Form
          </div>
        </div>

        {/* BANNER */}
        <div
          className="banner"
          style={{
            width: "100%",
            height: "320px",
            borderRadius: "28px",
            marginBottom: "30px",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <img
            src={machinePics[0]}
            alt=""
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />

          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(to right, rgba(0,0,0,0.7), rgba(0,0,0,0.2))",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              textAlign: "center",
              padding: "20px",
            }}
          >
            <div
              style={{
                color: "#ffffff",
                fontSize: "clamp(32px, 6vw, 64px)",
                fontWeight: "900",
                letterSpacing: "2px",
              }}
            >
              COSMOS AUTOMATION
            </div>

            <div
              style={{
                marginTop: "12px",
                color: "#f8f9fa",
                fontSize: "clamp(18px, 3vw, 28px)",
                fontWeight: "600",
              }}
            >
              Smart Manufacturing | Smart Workflow | Smart Tracking
            </div>
          </div>
        </div>

        {/* HEADER */}
        <div
          style={{
            textAlign: "center",
            marginBottom: "35px",
          }}
        >
          <h2
            style={{
              margin: "0",
              color: "#ffffff",
              fontSize: "clamp(30px, 5vw, 48px)",
              fontWeight: "900",
            }}
          >
            Work Entry Form
          </h2>
        </div>

        {/* FORM */}
        <div
          className="form-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2,1fr)",
            gap: "24px",
          }}
        >

{/* SELECT PERSON (input + add/remove stacked below) */}
<div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
  <SearchableSelect
    name="person"
    value={form.person}
    onChange={(e) => {
      handleChange(e);
      setSelectedPerson(e.target.value);
    }}
    options={persons}
    placeholder="Select Person"
    style={inputStyle}
  />

  <input
    type="text"
    value={newPerson}
    onChange={(e) => setNewPerson(e.target.value)}
    placeholder="Enter Person Name"
    style={{
      width: "180px",
      height: "30px",
      fontSize: "12px",
      padding: "5px",
    }}
  />

  <div
    style={{
      display: "flex",
      gap: "10px",
      alignItems: "center",
    }}
  >
    <button
      type="button"
      onClick={handleAddPerson}
      style={{
        width: "70px",
        height: "30px",
        fontSize: "12px",
        cursor: "pointer",
      }}
    >
      Add
    </button>

    <button
      type="button"
      onClick={handleDeletePerson}
      style={{
        width: "70px",
        height: "30px",
        fontSize: "12px",
        cursor: "pointer",
      }}
    >
      Remove
    </button>
  </div>
</div>

{/* SELECT PROJECT (input + add/remove stacked below) */}
<div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
  <SearchableSelect
    name="project"
    value={form.project}
    onChange={handleChange}
    options={projects}
    placeholder="Select Project"
    style={inputStyle}
  />

  <input
    type="text"
    value={newProject}
    onChange={(e) => setNewProject(e.target.value)}
    placeholder="Enter Project"
    style={{
      width: "180px",
      height: "30px",
      fontSize: "12px",
      padding: "5px",
    }}
  />

  <div
    style={{
      display: "flex",
      gap: "10px",
      alignItems: "center",
    }}
  >
    <button
      type="button"
      onClick={addProject}
      style={{
        width: "70px",
        height: "30px",
        fontSize: "12px",
        cursor: "pointer",
      }}
    >
      Add
    </button>

    <button
      type="button"
      onClick={deleteProject}
      style={{
        width: "70px",
        height: "30px",
        fontSize: "12px",
        cursor: "pointer",
      }}
    >
      Remove
    </button>
  </div>
</div>


{/* SELECT WORK (input + add/remove stacked below) */}
<div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
  <SearchableSelect
    name="work"
    value={form.work}
    onChange={handleChange}
    options={works}
    placeholder="Select Work"
    style={inputStyle}
  />

  <input
    type="text"
    value={newWork}
    onChange={(e) => setNewWork(e.target.value)}
    placeholder="Enter Work"
    style={{
      width: "180px",
      height: "30px",
      fontSize: "12px",
      padding: "5px",
    }}
  />

  <div
    style={{
      display: "flex",
      gap: "10px",
      alignItems: "center",
    }}
  >
    <button
      type="button"
      onClick={addWork}
      style={{
        width: "70px",
        height: "30px",
        fontSize: "12px",
        cursor: "pointer",
      }}
    >
      Add
    </button>

    <button
      type="button"
      onClick={removeWork}
      style={{
        width: "70px",
        height: "30px",
        fontSize: "12px",
        cursor: "pointer",
      }}
    >
      Remove
    </button>
  </div>
</div>


{/* SELECT MACHINE (input + add/remove stacked below, buttons in one row) */}
<div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
  <SearchableSelect
    name="machine"
    value={form.machine}
    onChange={handleChange}
    options={machines}
    placeholder="Select Machine"
    style={inputStyle}
  />

  <input
    type="text"
    value={newMachine}
    onChange={(e) => setNewMachine(e.target.value)}
    placeholder="Enter Machine"
    style={{
      width: "180px",
      height: "30px",
      fontSize: "12px",
      padding: "5px",
    }}
  />

  <div
    style={{
      display: "flex",
      gap: "10px",
      alignItems: "center",
    }}
  >
    <button
      type="button"
      onClick={addMachine}
      style={{
        width: "70px",
        height: "30px",
        fontSize: "12px",
        cursor: "pointer",
      }}
    >
      ADD
    </button>

    <button
      type="button"
      onClick={deleteMachine}
      style={{
        width: "70px",
        height: "30px",
        fontSize: "12px",
        cursor: "pointer",
      }}
    >
      REMOVE
    </button>
  </div>
</div>

{/* SELECT SHIFT (input + add/remove stacked below) */}
<div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
  <SearchableSelect
    name="shift_name"
    value={form.shift_name}
    onChange={handleChange}
    options={shifts}
    placeholder="Select Shift"
    style={inputStyle}
  />

  <input
    type="text"
    value={newShift}
    onChange={(e) => setNewShift(e.target.value)}
    placeholder="Enter Shift"
    style={{
      width: "180px",
      height: "30px",
      fontSize: "12px",
      padding: "5px",
    }}
  />

  <div
    style={{
      display: "flex",
      gap: "10px",
      alignItems: "center",
    }}
  >
    <button
      type="button"
      onClick={addShift}
      style={{
        width: "70px",
        height: "30px",
        fontSize: "12px",
        cursor: "pointer",
      }}
    >
      Add
    </button>

    <button
      type="button"
      onClick={removeShift}
      style={{
        width: "70px",
        height: "30px",
        fontSize: "12px",
        cursor: "pointer",
      }}
    >
      Remove
    </button>
  </div>
</div>

{/* SELECT AREA (input + add/remove stacked below, buttons in one row) */}
<div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
  <SearchableSelect
    name="area"
    value={form.area}
    onChange={(e) => {
      handleChange(e);
      setSelectedArea(e.target.value);
    }}
    options={areas}
    placeholder="Select Area"
    style={inputStyle}
  />

  <input
    type="text"
    value={newArea}
    onChange={(e) => setNewArea(e.target.value)}
    placeholder="Enter New Area"
    style={{
      width: "180px",
      height: "30px",
      fontSize: "12px",
      padding: "5px",
    }}
  />

  <div
    style={{
      display: "flex",
      gap: "10px",
      alignItems: "center",
    }}
  >
    <button
      type="button"
      onClick={addArea}
      style={{
        width: "80px",
        height: "35px",
        cursor: "pointer",
        fontWeight: "bold",
      }}
    >
      ADD
    </button>

    <button
      type="button"
      onClick={removeArea}
      style={{
        width: "90px",
        height: "35px",
        cursor: "pointer",
        fontWeight: "bold",
        background: "#fff",
        color: "#000",
        border: "none",
        borderRadius: "4px",
      }}
    >
      REMOVE
    </button>
  </div>
</div>



{/* CONSUMED ITEM (input + add/remove stacked below) */}
<div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
  <SearchableSelect
    name="consumed"
    value={form.consumed}
    onChange={handleChange}
    options={consumedItems}
    placeholder="Consumed Item"
    style={inputStyle}
  />

  <input
    type="text"
    value={newConsumedItem}
    onChange={(e) => setNewConsumedItem(e.target.value)}
    placeholder="Enter Consumed Item"
    style={{
      width: "180px",
      height: "30px",
      fontSize: "12px",
      padding: "5px",
    }}
  />

  <div
    style={{
      display: "flex",
      gap: "10px",
      alignItems: "center",
    }}
  >
    <button
      type="button"
      onClick={addConsumedItem}
      style={{
        width: "70px",
        height: "30px",
        fontSize: "12px",
        cursor: "pointer",
      }}
    >
      Add
    </button>

    <button
      type="button"
      onClick={removeConsumedItem}
      style={{
        width: "70px",
        height: "30px",
        fontSize: "12px",
        cursor: "pointer",
      }}
    >
      Remove
    </button>
  </div>
</div>

{/* MATERIAL CONSUMED (free text description) */}
<div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
  <div style={labelStyle}>Material Consumed</div>

  <textarea
    name="materialConsumed"
    placeholder="Enter Material Consumed Description"
    value={form.materialConsumed}
    onChange={handleChange}
    rows="4"
    style={{
      ...inputStyle,
      resize: "none",
      minHeight: "140px",
    }}
  />
</div>

{/* SELECT WORK STATUS (input + add/remove stacked below) */}
<div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
  <SearchableSelect
    name="status"
    value={form.status}
    onChange={handleChange}
    options={workStatuses}
    placeholder="Select Work Status"
    style={inputStyle}
  />

  <input
    type="text"
    value={newWorkStatus}
    onChange={(e) => setNewWorkStatus(e.target.value)}
    placeholder="Enter Work Status"
    style={{
      width: "180px",
      height: "30px",
      fontSize: "12px",
      padding: "5px",
    }}
  />

  <div
    style={{
      display: "flex",
      gap: "10px",
      alignItems: "center",
    }}
  >
    <button
      type="button"
      onClick={addWorkStatus}
      style={{
        width: "70px",
        height: "30px",
        fontSize: "12px",
        cursor: "pointer",
      }}
    >
      Add
    </button>

    <button
      type="button"
      onClick={removeWorkStatus}
      style={{
        width: "70px",
        height: "30px",
        fontSize: "12px",
        cursor: "pointer",
      }}
    >
      Remove
    </button>
  </div>
</div>

          <input
            type="text"
            name="workDescription"
            placeholder="Enter Detailed Work Description"
            value={form.workDescription}
            onChange={handleChange}
            style={inputStyle}
          />

          <div>
            <div style={labelStyle}>Start Time</div>

            <input
              type="time"
              name="startTime"
              value={form.startTime}
              onChange={handleChange}
              style={inputStyle}
            />
          </div>

          <div>
            <div style={labelStyle}>End Time</div>

            <input
              type="time"
              name="endTime"
              value={form.endTime}
              onChange={handleChange}
              style={inputStyle}
            />
          </div>
          
{/* ACTIVITIES (input + add/remove stacked below) */}
<div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
  <SearchableSelect
    name="designActivity"
    value={form.designActivity}
    onChange={handleChange}
    options={designActivities}
    placeholder="Activities"
    style={inputStyle}
  />

  <input
    type="text"
    value={newDesignActivity}
    onChange={(e) => setNewDesignActivity(e.target.value)}
    placeholder="Enter Activity"
    style={{
      width: "180px",
      height: "30px",
      fontSize: "12px",
      padding: "5px",
    }}
  />

  <div
    style={{
      display: "flex",
      gap: "10px",
      alignItems: "center",
    }}
  >
    <button
      type="button"
      onClick={addDesignActivity}
      style={{
        width: "70px",
        height: "30px",
        fontSize: "12px",
        cursor: "pointer",
      }}
    >
      Add
    </button>

    <button
      type="button"
      onClick={removeDesignActivity}
      style={{
        width: "70px",
        height: "30px",
        fontSize: "12px",
        cursor: "pointer",
      }}
    >
      Remove
    </button>
  </div>
</div>

          <input
            type="number"
            name="quantity"
            placeholder="Quantity"
            value={form.quantity}
            onChange={handleChange}
            style={inputStyle}
          />

          <input
            type="number"
            name="rejection"
            placeholder="Rejection"
            value={form.rejection}
            onChange={handleChange}
            style={inputStyle}
          />

{/* SELECT EQUIPMENT (input + add/remove stacked below) */}
<div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
  <SearchableSelect
    name="equipment"
    value={form.equipment}
    onChange={handleChange}
    options={equipments}
    placeholder="Select Equipment"
    style={inputStyle}
  />

  <input
    type="text"
    value={newEquipment}
    onChange={(e) => setNewEquipment(e.target.value)}
    placeholder="Enter Equipment"
    style={{
      width: "180px",
      height: "30px",
      fontSize: "12px",
      padding: "5px",
    }}
  />

  <div
    style={{
      display: "flex",
      gap: "10px",
      alignItems: "center",
    }}
  >
    <button
      type="button"
      onClick={addEquipment}
      style={{
        width: "70px",
        height: "30px",
        fontSize: "12px",
        cursor: "pointer",
      }}
    >
      ADD
    </button>

    <button
      type="button"
      onClick={deleteEquipment}
      style={{
        width: "70px",
        height: "30px",
        fontSize: "12px",
        cursor: "pointer",
      }}
    >
      REMOVE
    </button>
  </div>
</div>

{/* WFH NAMES (input + add/remove stacked below) */}
<div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
  <SearchableSelect
    name="wfhName"
    value={form.wfhName}
    onChange={handleChange}
    options={wfhNames}
    placeholder="WFH Names"
    style={inputStyle}
  />

  <input
    type="text"
    value={newWfhName}
    onChange={(e) => setNewWfhName(e.target.value)}
    placeholder="Enter WFH Name"
    style={{
      width: "180px",
      height: "30px",
      fontSize: "12px",
      padding: "5px",
    }}
  />

  <div
    style={{
      display: "flex",
      gap: "10px",
      alignItems: "center",
    }}
  >
    <button
      type="button"
      onClick={addWfhName}
      style={{
        width: "70px",
        height: "30px",
        fontSize: "12px",
        cursor: "pointer",
      }}
    >
      Add
    </button>

    <button
      type="button"
      onClick={removeWfhName}
      style={{
        width: "70px",
        height: "30px",
        fontSize: "12px",
        cursor: "pointer",
      }}
    >
      Remove
    </button>
  </div>
</div>

        </div>

        {/* REMARKS */}
        <div style={{ marginTop: "25px" }}>
          <div style={labelStyle}>Remarks</div>

          <textarea
            name="remarks"
            placeholder="Enter Complete Remarks Here"
            value={form.remarks}
            onChange={handleChange}
            rows="5"
            style={{
              ...inputStyle,
              resize: "none",
              minHeight: "180px",
            }}
          />
        </div>




        <div
  style={{
    marginTop: "30px",
    padding: "20px",
    borderRadius: "15px",
    background: "rgba(255,255,255,0.08)",
  }}
>
  
</div>

  













        {/* MACHINE ICONS */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "25px",
            flexWrap: "wrap",
            marginTop: "30px",
          }}
        >
          {machineImages.map((img, index) => (
            <img
              key={index}
              src={img}
              alt=""
              style={{
                width: "80px",
                animation: "float 6s infinite",
                filter: "drop-shadow(0 0 12px rgba(255,234,0,0.6))",
              }}
            />
          ))}
        </div>

        {/* SUBMIT BUTTON */}
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <button
            onClick={handleSubmit}
            className="submit-btn"
            style={{
              width: "40%",
              minWidth: "260px",
              marginTop: "35px",
              padding: "24px",
              border: "none",
              borderRadius: "18px",
              background:
                "linear-gradient(135deg,#7b2cbf,#9d4edd,#c77dff)",
              color: "#fff",
              fontSize: "30px",
              fontWeight: "800",
              cursor: "pointer",
              transition: "0.3s",
              boxShadow: "0 12px 28px rgba(255,234,0,0.35)",
            }}
          >
            Submit
          </button>
        </div>

    <div style={{ padding: "20px" }}>
     
      <button onClick={handleDownloadClick}>Download CSV</button>
    </div>

    



      </div>

      {/* STYLES */}
      <style>
        {`
          *{
            box-sizing:border-box;
          }

          @keyframes float {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-18px); }
            100% { transform: translateY(0px); }
          }

          @keyframes floatBot {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
            100% { transform: translateY(0px); }
          }

          select option {
            color: black;
            background: white;
            font-size: 20px;
          }

          .searchable-option:hover {
            background: rgba(255,234,0,0.22) !important;
          }

          input::placeholder,
          textarea::placeholder {
            color: rgba(255,255,255,0.80);
            font-size: 20px;
            font-weight: 600;
          }

          input:focus,
          select:focus,
          textarea:focus {
            border: 1px solid #ffea00;
            box-shadow: 0 0 20px rgba(255,234,0,0.9);
          }

          button:hover {
            transform: scale(1.02);
            box-shadow: 0 0 35px rgba(255,234,0,1);
            background: linear-gradient(135deg,#ffd60a,#ffea00,#ffee32);
            color: #000;
          }

          body {
            margin: 0;
            overflow-x: hidden;
            font-family: Arial, sans-serif;
            background: #000;
            cursor: default;
          }

          @media screen and (max-width: 1200px) {

            .main-card{
              padding:25px !important;
            }

            .form-grid {
              grid-template-columns: 1fr 1fr !important;
            }
          }

          @media screen and (max-width: 900px) {

            .form-grid {
              grid-template-columns: 1fr !important;
            }

            .form-grid select,
            .form-grid input,
            textarea {
              font-size: 20px !important;
              min-height: 82px !important;
              padding: 22px !important;
            }

            .submit-btn{
              width:100% !important;
              font-size:26px !important;
            }

            .assistant-box{
              transform:scale(0.9);
              right:5px !important;
              bottom:5px !important;
            }
          }

          @media screen and (max-width: 600px) {

            .main-card{
              padding:16px !important;
              border-radius:22px !important;
            }

            .banner{
              height:220px !important;
            }

            .assistant-box{
              display:none !important;
            }

            .form-grid{
              gap:18px !important;
            }

            .form-grid select,
            .form-grid input,
            textarea {
              font-size:18px !important;
              min-height:76px !important;
              padding:20px !important;
            }

            input::placeholder,
            textarea::placeholder {
              font-size: 17px !important;
            }

            .submit-btn{
              width:100% !important;
              min-width:100% !important;
              padding:20px !important;
              font-size:24px !important;
            }
          }
        `}
      </style>
   


    </div>
  );
};

export default CosmoAutomation;